import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

// Inline-editable exclusion row with native HTML5 drag-drop reorder.
function ExclusionRow({ excl, index, onSave, onDelete, canDelete, isNew, onCancelNew, dragProps }) {
  const [editing, setEditing] = useState(isNew);
  const [value, setValue] = useState(excl.text || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const text = (value || '').trim();
    if (!text) {
      if (isNew) { onCancelNew(); return; }
      setValue(excl.text);
      setEditing(false);
      return;
    }
    if (!isNew && text === excl.text) { setEditing(false); return; }
    try {
      await onSave(text);
      setEditing(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(msg);
    }
  };

  const cancel = () => {
    if (isNew) { onCancelNew(); return; }
    setValue(excl.text);
    setEditing(false);
  };

  return (
    <div
      className="flex items-center gap-2 group border border-border rounded-[4px] px-2 py-1.5 bg-card hover:bg-secondary/10 transition-colors"
      data-testid={`exclusion-row-${index}`}
      {...dragProps}
    >
      <div
        className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground py-1"
        data-testid={`exclusion-drag-${index}`}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            className="w-full text-xs bg-transparent border-none outline-none focus:outline-none focus:ring-1 focus:ring-primary/50 rounded-[4px] px-1 py-0.5"
            data-testid={`exclusion-input-${index}`}
          />
        ) : (
          <button
            type="button"
            onDoubleClick={() => setEditing(true)}
            className="w-full text-left text-xs py-0.5 cursor-text"
            title="Double-click to edit"
            data-testid={`exclusion-text-${index}`}
          >
            {excl.text}
          </button>
        )}
      </div>
      {canDelete && !editing && (
        <button
          type="button"
          className="opacity-0 group-hover:opacity-100 p-1 rounded-[4px] hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-opacity"
          onClick={onDelete}
          data-testid={`exclusion-delete-${index}`}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function ExclusionsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const dragIdx = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/exclusions-library').then(r => setItems(r.data)).catch(() => toast.error('Failed to load exclusions')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ─── Native HTML5 drag-drop reorder ──────────────────────────────────
  const handleDragStart = (i) => (e) => {
    dragIdx.current = i;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(i)); // required for Firefox
  };
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (targetIdx) => async (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === targetIdx) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    // Re-number sort_order for stable persistence
    const withOrder = next.map((it, idx) => ({ ...it, sort_order: idx + 1 }));
    setItems(withOrder);
    try {
      await api.put('/exclusions-library/reorder', {
        items: withOrder.map(it => ({ id: it.id, sort_order: it.sort_order })),
      });
    } catch {
      toast.error('Reorder failed');
      load();
    }
  };

  const saveExisting = (excl) => async (newText) => {
    const updated = await api.put(`/exclusions-library/${excl.id}`, { text: newText });
    setItems(prev => prev.map(x => x.id === excl.id ? updated.data : x));
  };

  const saveNew = async (text) => {
    const r = await api.post('/exclusions-library', { text });
    setItems(prev => [...prev, r.data]);
    setNewRow(false);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/exclusions-library/${confirmDelete.id}`);
      setItems(prev => prev.filter(x => x.id !== confirmDelete.id));
      toast.success('Exclusion removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return <Card className="border-border bg-card shadow-none"><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>;
  }

  return (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="p-3 space-y-1.5">
        {items.length === 0 && !newRow && (
          <div className="text-center py-8 text-xs text-muted-foreground">
            No exclusions yet. Click &quot;Add Exclusion&quot; to create the first one.
          </div>
        )}
        {items.map((it, i) => (
          <ExclusionRow
            key={it.id}
            excl={it}
            index={i}
            onSave={saveExisting(it)}
            onDelete={() => setConfirmDelete(it)}
            canDelete={canEdit}
            isNew={false}
            onCancelNew={() => {}}
            dragProps={{
              draggable: true,
              onDragStart: handleDragStart(i),
              onDragOver: handleDragOver(i),
              onDrop: handleDrop(i),
            }}
          />
        ))}
        {newRow && (
          <ExclusionRow
            excl={{ text: '' }}
            index={items.length}
            onSave={saveNew}
            onDelete={() => {}}
            canDelete={false}
            isNew={true}
            onCancelNew={() => setNewRow(false)}
            dragProps={{}}
          />
        )}
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8 text-muted-foreground hover:text-primary"
            onClick={() => setNewRow(true)}
            disabled={newRow}
            data-testid="exclusions-add-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Exclusion
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="exclusion-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this exclusion?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the exclusion from the library permanently. Existing proposals are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
